import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification as NotificationEntity } from 'app/layout/common/notifications/notifications.types';
import {
    Notification as PanelNotification,
    NotificationPanelComponent,
} from 'app/shared/components/notification-panel/notification-panel.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [
        MatButtonModule,
        MatIconModule,
        NotificationPanelComponent,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: NotificationEntity[] = [];
    panelNotifications: PanelNotification[] = [];
    unreadCount: number = 0;
    private _panelIdToNotificationId: Map<number, string> = new Map();
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: NotificationEntity[]) => {
                // Load the notifications
                this.notifications = notifications ?? [];
                this._mapNotificationsForPanel(this.notifications);

                // Calculate the unread count
                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef?.detach();
    }

    /**
     * Sync UI-only panel state changes
     */
    onPanelNotificationsChange(notifications: PanelNotification[]): void {
        this.panelNotifications = notifications;
        this.unreadCount = notifications.filter(
            (notification) => !notification.read
        ).length;
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Persist single read action from panel
     */
    onPanelNotificationRead(panelId: number): void {
        const notification = this._getNotificationForPanelId(panelId);

        if (!notification || notification.read) {
            return;
        }

        this._notificationsService
            .update(notification.id, { ...notification, read: true })
            .subscribe();
    }

    /**
     * Persist mark-all-read action from panel
     */
    onPanelMarkAllRead(): void {
        this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Persist delete action from panel
     */
    onPanelNotificationDelete(panelId: number): void {
        const notification = this._getNotificationForPanelId(panelId);

        if (!notification) {
            return;
        }

        this._notificationsService.delete(notification.id).subscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Map backend notifications into the reusable panel data model.
     */
    private _mapNotificationsForPanel(notifications: NotificationEntity[]): void {
        this._panelIdToNotificationId.clear();

        this.panelNotifications = notifications.map((notification, index) => {
            const panelId = index + 1;
            this._panelIdToNotificationId.set(panelId, notification.id);

            const parts = this._formatNotificationDateTime(notification.time);

            return {
                id: panelId,
                title: this._toPlainText(notification.title) || 'Notification',
                desc: this._toPlainText(notification.description),
                date: parts.date,
                time: parts.time,
                read: Boolean(notification.read),
            };
        });
    }

    /**
     * Resolve a panel row id back to the original notification entity.
     */
    private _getNotificationForPanelId(
        panelId: number
    ): NotificationEntity | null {
        const notificationId = this._panelIdToNotificationId.get(panelId);

        if (!notificationId) {
            return null;
        }

        return (
            this.notifications.find(
                (notification) => notification.id === notificationId
            ) ?? null
        );
    }

    /**
     * Convert service time into panel date and time strings.
     */
    private _formatNotificationDateTime(time: string): {
        date: string;
        time: string;
    } {
        const parsed = new Date(time);

        if (Number.isNaN(parsed.getTime())) {
            return {
                date: 'Unknown date',
                time: 'Unknown time',
            };
        }

        return {
            date: parsed.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            }),
            time: parsed.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
            }),
        };
    }

    /**
     * Strip HTML tags because the reusable panel renders plain text.
     */
    private _toPlainText(value?: string): string {
        if (!value) {
            return '';
        }

        return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        this.unreadCount = this.notifications.filter(
            (notification) => !notification.read
        ).length;
    }
}
